import { useMutation, useQuery } from '@apollo/client';
import { Hub } from '@mui/icons-material';
import { Box, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import { Duration } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { boolean, number, object, string } from 'yup';
import { applyServerErrors, useNotifications } from '~/api/helpers';
import { SaveButton } from '~/components/button';
import { DetailSelect, DetailSwitch, DetailTextField } from '~/components/forms/details';
import { NumberInput } from '~/components/inputs/NumberInput/NumberInput';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import { FormLayout, type FormTableData } from '~/components/settings';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { PptTransition } from '~/generated/graphql';
import { i18n } from '~/lib/i18n';
import { NetworkSettingsDocument, UpdateNetworkDocument } from './NetworkSettings.generated';

// PowerPoint slideshow transitions come from the PptTransition GraphQL enum
// (NetworkSettings::PPT_TRANSITIONS on the backend); labels are resolved from
// i18n at render time.
const PPT_TRANSITIONS = Object.values(PptTransition);

const validationSchema = object({
  enableApi: boolean().required(),
  enableSnmp: boolean().required(),
  encryptionKey: string().required(
    i18n.t('pages.network_settings.validation.encryption_key_required'),
  ),
  name: string().required(i18n.t('common.validation.name_required')),
  settings: object({
    defaultImageDuration: number()
      .required(i18n.t('pages.network_settings.validation.default_image_duration_required'))
      .min(5, i18n.t('pages.network_settings.validation.default_image_duration_min'))
      .max(60 * 60, i18n.t('pages.network_settings.validation.default_image_duration_max')),
    pptSlideDuration: number()
      .required(i18n.t('pages.network_settings.validation.ppt_slide_duration_required'))
      .min(1, i18n.t('pages.network_settings.validation.ppt_slide_duration_min'))
      .max(60, i18n.t('pages.network_settings.validation.ppt_slide_duration_max')),
    pptTransition: string().oneOf(PPT_TRANSITIONS).required(),
    enableLanguageSelection: boolean().required(),
    enableLiveChannelStreaming: boolean().required(),
    enableWebpRendition: boolean().required(),
    managePropertyGroups: boolean().required(),
    manageViews: boolean().required(),
    accessFcAdminLeagues: boolean().required(),
    showTimestampOnScreenshots: boolean().required(),
    transcodeServerHost: string()
      .matches(
        /^(?:\d{1,3}\.){3}\d{1,3}$/,
        i18n.t('pages.network_settings.validation.transcode_server_host_format'),
      )
      .nullable(),
  }).required(),
  snmpCommunity: string()
    .trim()
    .max(32, i18n.t('pages.network_settings.validation.snmp_community_max'))
    .matches(/^[!-~]+$/, {
      excludeEmptyString: true,
      message: i18n.t('pages.network_settings.validation.snmp_community_format'),
    })
    .when('enableSnmp', {
      is: true,
      then: (schema) =>
        schema.required(i18n.t('pages.network_settings.validation.snmp_community_required')),
    }),
  timeZone: string().required(i18n.t('pages.network_settings.validation.time_zone_required')),
});

export const NetworkSettings = () => {
  const { currentNetwork, currentUser } = useAppContext();
  const { t } = useTranslation();

  const { data, loading, error } = useQuery(NetworkSettingsDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const [updateNetwork] = useMutation(UpdateNetworkDocument, {
    ...useNotifications(UpdateNetworkDocument, {
      failure: (error) => error.message || t('api.common.error_generic'),
      success: t('api.network.update_success'),
    }),
  });

  const initialValues = useMemo(() => {
    const networkSettings = data?.network?.settings;
    return {
      enableApi: data?.network?.enableApi ?? false,
      enableSnmp: data?.network?.enableSnmp ?? false,
      encryptionKey: data?.network?.encryptionKey ?? '',
      name: data?.network?.name ?? '',
      settings: {
        defaultImageDuration: networkSettings?.defaultImageDuration
          ? Duration.fromISO(networkSettings.defaultImageDuration).as('seconds')
          : null,
        pptSlideDuration: networkSettings?.pptSlideDuration ?? 8,
        pptTransition: networkSettings?.pptTransition ?? PptTransition.Fade,
        enableLanguageSelection: networkSettings?.enableLanguageSelection ?? false,
        enableLiveChannelStreaming: networkSettings?.enableLiveChannelStreaming ?? false,
        enableWebpRendition: networkSettings?.enableWebpRendition ?? false,
        managePropertyGroups: networkSettings?.managePropertyGroups ?? false,
        manageViews: networkSettings?.manageViews ?? false,
        accessFcAdminLeagues: networkSettings?.accessFcAdminLeagues ?? true,
        showTimestampOnScreenshots: networkSettings?.showTimestampOnScreenshots ?? true,
        transcodeServerHost: networkSettings?.transcodeServerHost ?? '',
      },
      snmpCommunity: data?.network?.snmpCommunity ?? '',
      timeZone: data?.network?.timeZone ?? '',
    };
  }, [data?.network]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values, { setErrors }) => {
      const validatedValues = validationSchema.cast(values);

      // Build settings object based on user role
      const settings: Record<string, unknown> = {
        enableWebpRendition: validatedValues.settings.enableWebpRendition,
        defaultImageDuration: Duration.fromObject({
          seconds: validatedValues.settings.defaultImageDuration,
        }).toISO(),
        pptSlideDuration: validatedValues.settings.pptSlideDuration,
        pptTransition: validatedValues.settings.pptTransition,
        showTimestampOnScreenshots: validatedValues.settings.showTimestampOnScreenshots,
      };

      // Only admins can toggle these features
      if (currentUser.admin) {
        settings.enableLanguageSelection = validatedValues.settings.enableLanguageSelection;
        settings.enableLiveChannelStreaming = validatedValues.settings.enableLiveChannelStreaming;
        settings.managePropertyGroups = validatedValues.settings.managePropertyGroups;
        settings.manageViews = validatedValues.settings.manageViews;
        settings.accessFcAdminLeagues = validatedValues.settings.accessFcAdminLeagues;
        // Admins can always update transcode host
        settings.transcodeServerHost = validatedValues.settings.transcodeServerHost || null;
      } else if (validatedValues.settings.enableLiveChannelStreaming) {
        // Network owners can update transcode host when feature is enabled
        // Don't send enableLiveChannelStreaming to preserve it on the backend
        settings.transcodeServerHost = validatedValues.settings.transcodeServerHost || null;
      }

      const result = await updateNetwork({
        variables: {
          networkId: currentNetwork.id,
          patch: {
            name: validatedValues.name,
            enableApi: validatedValues.enableApi,
            enableSnmp: validatedValues.enableSnmp,
            encryptionKey: validatedValues.encryptionKey,
            settings,
            snmpCommunity: validatedValues.snmpCommunity || null,
            timeZone: validatedValues.timeZone,
          },
        },
      });
      applyServerErrors(result, setErrors);
    },
    validateOnMount: true,
    validationSchema,
  });

  const generalSettings = useMemo(
    () => [
      {
        heading: t('common.labels.name'),
        subHeading: t('pages.network_settings.general.name.sub_heading'),
        dataField: (
          <DetailTextField
            autoFocus
            disabled={formik.isSubmitting || !data?.network}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            name="name"
            value={formik.values.name}
          />
        ),
      },
      {
        heading: t('pages.network_settings.general.time_zone.heading'),
        subHeading: t('pages.network_settings.general.time_zone.sub_heading'),
        dataField: (
          <DetailSelect
            disabled={formik.isSubmitting || !data?.network}
            name="timeZone"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.timeZone}
          >
            {data?.timeZones.map(({ id, name }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </DetailSelect>
        ),
      },
      ...(data?.network?.deviceLicenses != null
        ? [
            {
              heading: t('pages.network_settings.general.device_licenses.heading'),
              subHeading: t('pages.network_settings.general.device_licenses.sub_heading'),
              dataField: <DetailTextField disabled fullWidth value={data.network.deviceLicenses} />,
            },
          ]
        : []),
    ],
    [data?.network, data?.timeZones, formik, t],
  );

  const imageSettings = useMemo(
    () => [
      {
        heading: t('pages.network_settings.images.default_image_duration.heading'),
        subHeading: t('pages.network_settings.images.default_image_duration.sub_heading'),
        dataField: (
          <NumberInput
            disabled={formik.isSubmitting || !data?.network}
            onBlur={formik.handleBlur}
            onChange={(_, val) => formik.setFieldValue('settings.defaultImageDuration', val)}
            value={formik.values.settings.defaultImageDuration}
            helperText={t('pages.network_settings.images.default_image_duration.helper_text')}
            min={5}
            max={60 * 60}
          />
        ),
      },
      {
        heading: t('pages.network_settings.images.enable_webp_rendition.heading'),
        subHeading: t('pages.network_settings.images.enable_webp_rendition.sub_heading'),
        dataField: (
          <DetailSwitch
            checked={formik.values.settings.enableWebpRendition}
            disabled={formik.isSubmitting || !data?.network}
            name="enableWebpRendition"
            onBlur={formik.handleBlur}
            onChange={(_, val) => formik.setFieldValue('settings.enableWebpRendition', val)}
            value={formik.values.settings.enableWebpRendition}
          />
        ),
      },
    ],
    [data?.network, formik, t],
  );

  const powerpointSettings = useMemo(
    () => [
      {
        heading: t('pages.network_settings.powerpoint.slide_duration.heading'),
        subHeading: t('pages.network_settings.powerpoint.slide_duration.sub_heading'),
        dataField: (
          <NumberInput
            disabled={formik.isSubmitting || !data?.network}
            onBlur={formik.handleBlur}
            onChange={(_, val) => formik.setFieldValue('settings.pptSlideDuration', val)}
            value={formik.values.settings.pptSlideDuration}
            helperText={t('pages.network_settings.powerpoint.slide_duration.helper_text')}
            min={1}
            max={60}
          />
        ),
      },
      {
        heading: t('pages.network_settings.powerpoint.transition.heading'),
        subHeading: t('pages.network_settings.powerpoint.transition.sub_heading'),
        dataField: (
          <DetailSelect
            disabled={formik.isSubmitting || !data?.network}
            name="settings.pptTransition"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.settings.pptTransition}
          >
            {PPT_TRANSITIONS.map((transition) => {
              // Kept on one line: the i18n-tasks PatternMapper regex for these
              // dynamic keys does not match a wrapped call.
              const labelKey = transition.toLowerCase();
              return (
                <MenuItem key={transition} value={transition}>
                  {t(`pages.network_settings.powerpoint.transition.options.${labelKey}`)}
                </MenuItem>
              );
            })}
          </DetailSelect>
        ),
      },
    ],
    [data?.network, formik, t],
  );

  const liveStreamingSettings = useMemo(() => {
    const settings: FormTableData[] = [
      {
        heading: t('pages.network_settings.general.encryption_key.heading'),
        subHeading: t('pages.network_settings.general.encryption_key.sub_heading'),
        dataField: (
          <DetailTextField
            disabled={formik.isSubmitting || !data?.network}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            name="encryptionKey"
            value={formik.values.encryptionKey}
            error={formik.touched.encryptionKey && Boolean(formik.errors.encryptionKey)}
            helperText={formik.touched.encryptionKey && formik.errors.encryptionKey}
          />
        ),
      },
    ];

    // Only admins can toggle the feature
    if (currentUser.admin) {
      settings.push({
        heading: t('pages.network_settings.live_streaming.enable.heading'),
        subHeading: t('pages.network_settings.live_streaming.enable.sub_heading'),
        dataField: (
          <DetailSwitch
            checked={formik.values.settings.enableLiveChannelStreaming}
            disabled={formik.isSubmitting || !data?.network}
            name="enableLiveChannelStreaming"
            onBlur={formik.handleBlur}
            onChange={(_, val) => formik.setFieldValue('settings.enableLiveChannelStreaming', val)}
            value={formik.values.settings.enableLiveChannelStreaming}
          />
        ),
      });
    }

    // Both admins and network owners can see and edit the transcode server host when feature is enabled
    if (formik.values.settings.enableLiveChannelStreaming) {
      settings.push({
        heading: t('pages.network_settings.live_streaming.transcode_server_host.heading'),
        // Network owners don't see the streaming toggle, so there's no parent to indent under.
        nested: currentUser.admin,
        subHeading: t('pages.network_settings.live_streaming.transcode_server_host.sub_heading'),
        dataField: (
          <DetailTextField
            disabled={formik.isSubmitting || !data?.network}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            name="settings.transcodeServerHost"
            value={formik.values.settings.transcodeServerHost}
            error={
              formik.touched.settings?.transcodeServerHost &&
              Boolean(formik.errors.settings?.transcodeServerHost)
            }
            helperText={
              formik.touched.settings?.transcodeServerHost &&
              formik.errors.settings?.transcodeServerHost
            }
            placeholder="192.168.1.1"
          />
        ),
      });
    }

    return settings;
  }, [currentUser.admin, data?.network, formik, t]);

  const deviceServicesSettings = useMemo(
    () => [
      {
        heading: t('pages.network_settings.device_services.enable_api.heading'),
        subHeading: t('pages.network_settings.device_services.enable_api.sub_heading'),
        dataField: (
          <DetailSwitch
            checked={formik.values.enableApi}
            disabled={formik.isSubmitting || !data?.network}
            name="enableApi"
            onBlur={formik.handleBlur}
            onChange={(_, val) => formik.setFieldValue('enableApi', val)}
            value={formik.values.enableApi}
          />
        ),
      },
      {
        heading: t('pages.network_settings.device_services.enable_snmp.heading'),
        subHeading: t('pages.network_settings.device_services.enable_snmp.sub_heading'),
        dataField: (
          <DetailSwitch
            checked={formik.values.enableSnmp}
            disabled={formik.isSubmitting || !data?.network}
            name="enableSnmp"
            onBlur={formik.handleBlur}
            onChange={(_, val) => void formik.setFieldValue('enableSnmp', val)}
            value={formik.values.enableSnmp}
          />
        ),
      },
      // Shown whatever the default: devices can run SNMP while new ones start without it.
      {
        heading: t('pages.network_settings.device_services.snmp_community.heading'),
        subHeading: t('pages.network_settings.device_services.snmp_community.sub_heading'),
        dataField: (
          <DetailTextField
            disabled={formik.isSubmitting || !data?.network}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            name="snmpCommunity"
            value={formik.values.snmpCommunity}
            error={formik.touched.snmpCommunity && Boolean(formik.errors.snmpCommunity)}
            helperText={formik.touched.snmpCommunity && formik.errors.snmpCommunity}
          />
        ),
      },
    ],
    [data?.network, formik, t],
  );

  const featureSettings = useMemo(
    () => [
      {
        heading: t('pages.network_settings.features.enable_language_selection.heading'),
        subHeading: t('pages.network_settings.features.enable_language_selection.sub_heading'),
        dataField: (
          <DetailSwitch
            checked={formik.values.settings.enableLanguageSelection}
            disabled={formik.isSubmitting || !data?.network}
            name="enableLanguageSelection"
            onBlur={formik.handleBlur}
            onChange={(_, val) => formik.setFieldValue('settings.enableLanguageSelection', val)}
            value={formik.values.settings.enableLanguageSelection}
          />
        ),
      },
      {
        heading: t('pages.network_settings.features.access_fc_admin_leagues.heading'),
        subHeading: t('pages.network_settings.features.access_fc_admin_leagues.sub_heading'),
        dataField: (
          <DetailSwitch
            checked={formik.values.settings.accessFcAdminLeagues}
            disabled={formik.isSubmitting || !data?.network}
            name="accessFcAdminLeagues"
            onBlur={formik.handleBlur}
            onChange={(_, val) => formik.setFieldValue('settings.accessFcAdminLeagues', val)}
            value={formik.values.settings.accessFcAdminLeagues}
          />
        ),
      },
      {
        heading: t('pages.network_settings.features.manage_property_groups.heading'),
        subHeading: t('pages.network_settings.features.manage_property_groups.sub_heading'),
        dataField: (
          <DetailSwitch
            checked={formik.values.settings.managePropertyGroups}
            disabled={formik.isSubmitting || !data?.network}
            name="managePropertyGroups"
            onBlur={formik.handleBlur}
            onChange={(_, val) => formik.setFieldValue('settings.managePropertyGroups', val)}
            value={formik.values.settings.managePropertyGroups}
          />
        ),
      },
      {
        heading: t('pages.network_settings.features.manage_views.heading'),
        subHeading: t('pages.network_settings.features.manage_views.sub_heading'),
        dataField: (
          <DetailSwitch
            checked={formik.values.settings.manageViews}
            disabled={formik.isSubmitting || !data?.network}
            name="manageViews"
            onBlur={formik.handleBlur}
            onChange={(_, val) => formik.setFieldValue('settings.manageViews', val)}
            value={formik.values.settings.manageViews}
          />
        ),
      },
      {
        heading: t('pages.network_settings.features.show_timestamp_on_screenshots.heading'),
        subHeading: t('pages.network_settings.features.show_timestamp_on_screenshots.sub_heading'),
        dataField: (
          <DetailSwitch
            checked={formik.values.settings.showTimestampOnScreenshots}
            disabled={formik.isSubmitting || !data?.network}
            name="showTimestampOnScreenshots"
            onBlur={formik.handleBlur}
            onChange={(_, val) => formik.setFieldValue('settings.showTimestampOnScreenshots', val)}
            value={formik.values.settings.showTimestampOnScreenshots}
          />
        ),
      },
    ],
    [data?.network, formik, t],
  );

  if ((!loading && !data) || error) return <Navigate replace to="/not-found" />;

  const saveButton = data?.network?.canUpdate.value && (
    <SaveButton
      disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
      type="submit"
      form="update-network"
    />
  );

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      <Toolbar
        actions={saveButton}
        titleIcon={<Hub />}
        titleText={t('pages.network_settings.title')}
      />
      <PageContainer>
        <Box
          component="form"
          id="update-network"
          onSubmit={formik.handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <FormLayout formHeading={t('common.labels.general')} formData={generalSettings} />
          <FormLayout
            formHeading={t('pages.network_settings.sections.images')}
            formData={imageSettings}
          />
          <FormLayout
            formHeading={t('pages.network_settings.sections.powerpoint')}
            formData={powerpointSettings}
          />
          <FormLayout
            formHeading={t('pages.network_settings.sections.live_streaming')}
            formData={liveStreamingSettings}
          />

          <FormLayout
            formHeading={t('pages.network_settings.sections.device_services')}
            formData={deviceServicesSettings}
          />

          {currentUser.admin && (
            <FormLayout
              formHeading={t('pages.network_settings.sections.features')}
              formData={featureSettings}
            />
          )}
        </Box>
      </PageContainer>
    </LoadingPane>
  );
};
